<template>
  <div class="ml-4 mr-4">
    <div v-if="type === 'create'">
      <div class="row py-3 align-items-center">
        <div class="col company-h1">
          <button type="button"
                  class="btn btn-back"
                  @click="setCompanyStep('companies-settings')"
          />
          Create Company
        </div>
        <div class="col text-right"
             v-if="$_can('companies.prices.store')"
        >
          <button class="btn btn-primary ld-ext-right"
                  type="button"
                  @click="savePrices(pricing)"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
      <companies-steps-menu type="create" />
    </div>
    <div v-if="type === 'edit'">
      <div class="row py-3 align-items-center company-header">
        <div class="col-4">
          <h2 class="company-h1 mr-3">
            <button type="button"
                    class="btn btn-back"
                    @click="$router.push('/companies')"
            />
            {{ company.name }}
          </h2>
          <template v-if="company.status">
            <span v-if="company.status.name === 'Active'"
                  :class="'btn-status btn-active'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Inactive'"
                  :class="'btn-status btn-closed'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Setup Incomplete'"
                  :class="'btn-status btn-ncover info-company'"
            >{{ company.status.name }}
              <div class="btn btn-link p-0 tippy-popup tippy-info"
                   @mouseover="showTooltipPrefix = true"
                   @mouseout="showTooltipPrefix = false"
              >
                <i class="fa fa-info-circle" />
              </div>
              <div class="popover tippy-info-popover"
                   v-if="showTooltipPrefix"
              >
                Setup incomplete is present when the company doesn't have one or more of the following: move sizes,
                arrival windows, merchant account, rates and coverage.
              </div>
            </span>
          </template>
        </div>
        <div class="col-8 d-flex align-items-center justify-content-end">
          <companies-visibility-bar
            :company="company"
            @saved="$emit('saved')"
            v-if="$_can('companies.review.request')"
          />
          <button type="button"
                  class="btn btn-primary ld-ext-right ml-5"
                  @click="savePrices(pricing)"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-2 pr-1">
        <div class="select-wrap amount-wrap">
          <label>Truck price</label>
          <span class="dollar">$</span>
          <input class="form-control"
                 type="text"
                 v-model="company.truck_price"
                 placeholder="Truck price"
                 :disabled="!$_can('companies.prices.store')"
          />
        </div>
      </div>
      <div class="col-2 pr-1">
        <div class="select-wrap amount-wrap">
          <label>Transaction fee</label>
          <span class="dollar">%</span>
          <input class="form-control"
                 type="text"
                 v-model="company.charge_fee"
                 placeholder="Transaction fee"
                 :disabled="!$_can('companies.prices.store')"
          />
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-7 border-right">
        <div class="row">
          <div class="col">
            <div class="l-header-2">Pricing</div>
          </div>
        </div>
        <template v-if="pricing.length > 0">
          <div v-for="(price, index) in pricing"
               :key="`price-prefix-${index}`"
               class="row"
          >
            <div class="col-2 pr-1">
              <div class="select-wrap">
                <label># of movers</label>
                <input class="form-control"
                       type="text"
                       v-model="pricing[index].mover_number"
                       placeholder="# of movers"
                       :disabled="!$_can('companies.prices.store')"
                />
              </div>
              <error-field :errors="errors" :field="`${index}.mover_number`" />
            </div>
            <div class="col-2 px-1">
              <div class="select-wrap amount-wrap">
                <label>Price</label>
                <span class="dollar">$</span>
                <input class="form-control"
                       type="text"
                       v-model="pricing[index].price"
                       :disabled="!$_can('companies.prices.store')"
                />
              </div>
              <error-field :errors="errors" :field="`${index}.price`" />
            </div>
            <div class="col-1 p-0 pt-4 text-center">
              +
            </div>
            <div class="col-1">
              <div class="">
                <label>Truck price</label>
                <p>$ {{ company.truck_price }}</p>
              </div>
            </div>
            <div class="col-1 p-0 pt-4 text-center">
              =
            </div>
            <div class="col-2 border-right">
              <div class="">
                <label />
                <h4>
                  $ {{ parseFloat(pricing[index].price) + parseFloat(company.truck_price)
                    ? parseFloat(pricing[index].price) + parseFloat(company.truck_price)
                    : null }}
                </h4>
              </div>
            </div>
            <div class="col-2">
              <div class="select-wrap amount-wrap">
                <label>Labor price</label>
                <span class="dollar">$</span>
                <input class="form-control"
                       type="text"
                       v-model="pricing[index].labor"
                       :disabled="!$_can('companies.prices.store')"
                />
              </div>
              <error-field :errors="errors" :field="`${index}.labor`" />
            </div>
            <div class="col-1"
                 v-if="pricing.length > 1"
            >
              <button class="btn btn-clear mt-3"
                      @click="removePrice(index)"
                      :disabled="!$_can('companies.prices.store')"
              >
                <span class="mod-icon-clear" />
              </button>
            </div>
          </div>
        </template>
        <div class="row"
             v-if="$_can('companies.prices.store')"
        >
          <div class="col">
            <button class="btn add-button"
                    type="button"
                    @click="addPricing"
            >
              <span><span class="mod-icon-add" /></span>
              Add Pricing Bracket
            </button>
          </div>
        </div>
      </div>
      <div class="col-5 mileage-percentages">
        <div class="row mb-3">
          <div class="col">
            <div class="row mb-3">
              <div class="col">
                <div class="l-header-2">Additional expenses</div>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col">
                <div class="row mb-3">
                  <div class="col">
                    <div class="select-wrap amount-wrap">
                      <label>Service fee</label>
                      <span class="dollar">$</span>
                      <input
                        class="form-control"
                        type="text"
                        v-model="company.service_fee"
                        placeholder="Service fee"
                        :disabled="!$_can('companies.prices.store')"
                      />
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <div class="select-wrap amount-wrap">
                      <label>Heavy items fee</label>
                      <span class="dollar">$</span>
                      <input
                        class="form-control"
                        type="text"
                        v-model="company.heavy_items_fee"
                        placeholder="Heavy items fee"
                        :disabled="!$_can('companies.prices.store')"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <div class="l-header-2">Mileage calculation</div>
          </div>
        </div>
        <div v-if="$_can('companies.configurations.store')">
          <div class="btn-group btn-group-toggle mb-3">
            <div @click="toggleCalculationType(1)">
              <input type="radio"
                     name="options"
                     autocomplete="off"
                     :checked="tempCompanyMileageTypeId === 1"
              /> Only deadhead length
            </div>
            <div @click="toggleCalculationType(2)">
              <input type="radio"
                     name="options"
                     autocomplete="off"
                     :checked="tempCompanyMileageTypeId === 2"
              /> Entire job length
            </div>
            <div @click="toggleCalculationType(3)">
              <input type="radio"
                     name="options"
                     autocomplete="off"
                     :checked="tempCompanyMileageTypeId === 3"
              /> Increase hourly rate
            </div>
          </div>
        </div>
        <template v-if="pricingLoading">
          <vue-content-loading
            :width="200"
            :height="150"
          >
            <rect x="0" y="0" rx="3" ry="3" width="70" height="10" />
            <rect x="80" y="0" rx="3" ry="3" width="100" height="10" />
            <rect x="190" y="0" rx="3" ry="3" width="10" height="10" />
            <rect x="0" y="20" rx="3" ry="3" width="130" height="10" />
            <rect x="155" y="20" rx="3" ry="3" width="130" height="10" />
          </vue-content-loading>
        </template>
        <template v-else>
          <div v-show="company.mileage_calculation_type_id === 1">
            <h6>
              Rate increase will calculate only the length when truck is running empty, deduct the free mileage and multiply the remaining distance with multiplier.
            </h6>
          </div>
          <div v-show="company.mileage_calculation_type_id === 2">
            <h6>
              Rate increase will calculate the total miles for a move and multiply it with the percentage entered, giving you a dollar amount which is added on top of the price per hour.
            </h6>
          </div>
          <div v-show="company.mileage_calculation_type_id === 3">
            <h6>
              Rate increase will calculate the entire length the truck travels. Multiplier you enter will create a percentage from the total miles, this percentage is then used to increase an hourly rate.
            </h6>
          </div>
          <template v-if="company.mileage_calculation_type_id === 2">
            <template v-if="mileageConfigurations.length > 0">
              <div v-for="(mileageConfiguration, index) in mileageConfigurations"
                   :key="`configuration-prefix-${index}`"
                   class="row mb-3"
              >
                <div class="col-3">
                  <div class="select-wrap">
                    <label>From (m)</label>
                    <input class="form-control"
                           type="text"
                           v-model="mileageConfigurations[index].from"
                           placeholder="From"
                           :disabled="!$_can('companies.prices.store')"
                    />
                  </div>
                  <error-field :errors="errors" :field="`${index}.from`" />
                </div>
                <div class="col-3">
                  <div class="select-wrap">
                    <label>To (m)</label>
                    <input class="form-control"
                           type="text"
                           v-model="mileageConfigurations[index].to"
                           placeholder="To"
                           :disabled="!$_can('companies.prices.store')"
                    />
                  </div>
                  <error-field :errors="errors" :field="`${index}.to`" />
                </div>
                <div class="col-3">
                  <div class="select-wrap">
                    <label>Percentage</label>
                    <input class="form-control"
                           type="text"
                           v-model="mileageConfigurations[index].percentage"
                           placeholder="%"
                           :disabled="!$_can('companies.prices.store')"
                    />
                  </div>
                  <error-field :errors="errors" :field="`${index}.percentage`" />
                </div>
                <div class="col-3">
                  <div class="col-1"
                       v-if="mileageConfigurations.length > 1"
                  >
                    <button class="btn btn-clear mt-3"
                            @click="removeMileageConfiguration(index)"
                            :disabled="!$_can('companies.prices.store')"
                    >
                      <span class="mod-icon-clear" />
                    </button>
                  </div>
                </div>
              </div>
            </template>
            <div class="row"
                 v-if="$_can('companies.prices.store')"
            >
              <div class="col">
                <button class="btn add-button"
                        type="button"
                        @click="addMileageConfiguration"
                >
                  <span><span class="mod-icon-add" /></span>
                  Add Bracket
                </button>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="row">
              <div class="col-6">
                <div class="select-wrap amount-wrap">
                  <label>Mileage multiplier</label>
                  <span class="percent">%</span>
                  <input class="form-control"
                         type="text"
                         v-model="company.mileage_multiplier"
                         placeholder="Mileage multiplier"
                         :disabled="!$_can('companies.prices.store')"
                  />
                </div>
              </div>
              <div class="col-6">
                <div class="select-wrap">
                  <label>Free mileage</label>
                  <input class="form-control"
                         type="text"
                         v-model="company.free_mileage"
                         placeholder="Free mileage"
                         :disabled="!$_can('companies.prices.store')"
                  />
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import toastr from 'toastr';
import { mapActions, mapMutations, mapState } from 'vuex';
import Errors from '../../classes/Errors';
import ErrorField from '../ErrorField';
import CompaniesStepsMenu from './CompaniesStepsMenu';
import CompaniesVisibilityBar from './CompaniesVisibilityBar';

export default {
  components: {
    CompaniesVisibilityBar,
    CompaniesStepsMenu,
    ErrorField,
  },
  props: {
    type: {
      type: String,
      default: 'edit',
    },
  },
  data: () => ({
    pricing: [
      {
        mover_number: '',
        price: '',
        labor: '',
      },
    ],
    mileageConfigurations: [
      {
        from: '',
        to: null,
        percentage: '',
      },
    ],
    tempCompanyMileageTypeId: null,
    pricingLoading: false,
    loadingButton: false,
    buttonDisabled: false,
    showTooltipPrefix: false,
    errors: new Errors(),
  }),
  computed: {
    ...mapState({
      company: state => state.companies.company,
    }),
  },
  mounted() {
    if (this.type === 'edit') {
      this.loadPrices();
      this.loadMileageConfigurations();
    }
    this.tempCompanyMileageTypeId = this.company.mileage_calculation_type_id;
  },
  methods: {
    ...mapActions(['getCompanies']),
    ...mapMutations(['setCompanyStep', 'setCompany']),
    addPricing() {
      this.pricing.push({
        mover_number: '',
        price: '',
        labor: '',
      });
    },
    removePrice(index) {
      const prices = this.pricing;
      prices.splice(index, 1);
      this.pricing = prices;
    },
    savePrices(pricing) {
      this.buttonDisabled = true;
      this.loadingButton = true;
      axios
        .post(`companies/${this.company.id}/movers/prices`, pricing)
        .then(() => {
          axios
            .patch(`companies/${this.company.id}`, {
              truck_price: this.company.truck_price,
              mileage_multiplier: this.company.mileage_multiplier,
              free_mileage: this.company.free_mileage,
              service_fee: this.company.service_fee,
              charge_fee: this.company.charge_fee,
              drive_back_addition: this.driveBackAdditionDefault,
              heavy_items_fee: this.company.heavy_items_fee,
            })
            .then(() => {
              if (this.company.mileage_calculation_type_id === 2) {
                axios
                  .post(
                    `companies/${this.company.id}/mileages/configurations`,
                    this.mileageConfigurations,
                  )
                  .catch(err => {
                    this.errors.record(err.response.data.error);
                  });
              }
              this.errors.clear();
              this.getCompanies();
              this.loadCompany();
              this.$emit('saved');
              if (this.type === 'edit') {
                toastr.success('Successfully saved pricing');
                this.errors.clear();
              } else {
                this.$router.push('/companies');
              }
            })
            .catch(err => {
              this.errors.record(err.response.data.error);
            });
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    loadPrices() {
      const { id } = this.$route.params;
      axios.get(`companies/${id}/movers/prices`).then(res => {
        this.pricing = res.data.data;
      });
    },
    loadCompany() {
      axios
        .get(`companies/${this.company.id}`)
        .then(res => {
          this.setCompany(res.data.data);
          this.tempCompanyMileageTypeId = res.data.data.mileage_calculation_type_id;
        })
        .finally(() => {
          this.pricingLoading = false;
        })
        .catch(err => {
          this.pricingLoading = false;
          this.errors.record(err.response.data.error);
        });
    },
    loadMileageConfigurations() {
      if (this.company.mileage_calculation_type_id === 2) {
        axios.get(`companies/${this.company.id}/mileages/configurations`).then(res => {
          this.mileageConfigurations = res.data.data;
        });
      }
    },
    addMileageConfiguration() {
      this.mileageConfigurations.push({
        from: '',
        to: null,
        percentage: '',
      });
    },
    removeMileageConfiguration(index) {
      const configurations = this.mileageConfigurations;
      configurations.splice(index, 1);
      this.mileageConfigurations = configurations;
    },
    toggleCalculationType(id) {
      this.pricingLoading = true;
      this.tempCompanyMileageTypeId = id;
      axios
        .patch(`companies/${this.company.id}`, {
          mileage_calculation_type_id: id,
        })
        .then(() => {
          this.loadCompany();
          if (this.company.mileage_calculation_type_id === 2) {
            this.loadMileageConfigurations();
          }
        })
        .catch(err => {
          this.pricingLoading = false;
          this.errors.record(err.response.data.error);
        });
    },
  },
};
</script>
<style lang="scss" scoped>
.btn-group-toggle {
  & div {
    padding: 7px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/companies/CompaniesPricing.vue